import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';

import { DialogService } from '../dialog.service';
import { SnackService } from '../snack.service';
import { EmailVerificationOutcome, NewAuthService } from './new-auth-service.service';

const RESEND_MESSAGES: Record<Exclude<EmailVerificationOutcome, 'alreadyVerified'>, { text: string; style: string }> = {
  sent: { text: 'Verification email sent! Please check your inbox (and spam folder).', style: 'success-snackbar' },
  throttled: {
    text: 'We already sent you a verification link. Please check your email (and spam folder).',
    style: 'warn-snackbar',
  },
  failed: {
    text: 'We could not send the verification email. Please try again or contact support.',
    style: 'error-snackbar',
  },
};

/**
 * The "email is not verified" prompt shown to a signed-in user whose Firebase email is unverified.
 *
 * Resending goes through `NewAuthService.sendMfaEmailVerification` (welby-micro-service
 * `auth/send-email-verification`), which emails a Firebase verification link to the account's own
 * address. The old resend linked to `/new-provider-signup?step=2`, whose `autoValidateEmail` call is
 * refused while that route is held, so its link could never verify anyone.
 *
 * Only one prompt is open at a time: every home page asks for it on init, and each request used to
 * stack another dialog, so one click on "resend" sent one email per dialog.
 */
@Injectable({ providedIn: 'root' })
export class EmailVerificationPromptService {
  private open = false;

  constructor(
    private auth: NewAuthService,
    private dialogService: DialogService,
    private snackService: SnackService
  ) {}

  async prompt(): Promise<void> {
    if (this.open) {
      return;
    }
    this.open = true;
    try {
      await this.promptUntilDone();
    } finally {
      this.open = false;
    }
  }

  private async promptUntilDone(): Promise<void> {
    for (;;) {
      const resend = await firstValueFrom(this.dialogService.openEmailNotVerfiedDialog().afterClosed());
      if (!resend) {
        await this.auth.signOut();
        return;
      }

      const outcome = await this.auth.sendMfaEmailVerification();
      if (outcome === 'alreadyVerified') {
        // Verified from the link, e.g. in another tab: the service already reloaded the Firebase user.
        this.auth.emailVerified = true;
        this.auth.setNeedsTakeOver(false);
        this.snackService.genericSnackBar('Your email is verified.', ['success-snackbar'], 5000);
        return;
      }
      const message = RESEND_MESSAGES[outcome];
      this.snackService.genericSnackBar(message.text, [message.style], 5000);
    }
  }
}
